import React, { createContext, useEffect, useContext, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatDate, parseDate } from 'utils/format';
import store from 'utils/store';
import produce from 'utils/produce';
import uuid from 'utils/uuid';
import exif from 'utils/exif';
import arrayMove from 'utils/arrayMove';
import debounce from 'utils/debounce';
import queryString from 'utils/queryString';
import uniqBy from 'utils/uniqBy';
import { isUrlValid } from 'utils/validator';
import useModalContext from 'components/context/modal';
import useUserContext from 'ecommerce/context/user';
import contains from 'utils/contains';
import findLastIndex from 'utils/findLastIndex';
import syncApi from 'ecommerce/api/sync';
import pickExcept from 'utils/pickExcept';
import { parseData } from 'editor-utils/dataParser';
import TOC from 'editor-utils/toc';
import {
  PLACEHOLDER_TYPE,
  getSpineSize,
  getTemplatePage,
  hasPageLayers,
} from 'editor-utils/template';
import imageApi from '../api/image';
import { isPhotoStillExist, getImagePreview, getImageSize } from '../lib/imageTransformer';
import { usePollingStore } from '../lib/pollingStore';

const DEFAULT_VALUE = {
  items: {},
  version: 6,
};

const EditorContext = createContext();

const UPDATED_AT_FORMAT = 'YYYY-MM-DD';

const isEditorItemExpired = (todayDate, updatedAt, expiration) => {
  const diff = parseDate(todayDate, UPDATED_AT_FORMAT) - parseDate(updatedAt, UPDATED_AT_FORMAT);
  const days = diff / (1000 * 3600 * 24);

  if (days > expiration) {
    return true;
  }

  return false;
};

/**
 * Function removes useless AWS signature from photo.URL
 * @param {object} photo
 * @returns {object}
 */
const fixPhotoObject = photo => {
  if (photo.mediumId && photo.url.indexOf('X-Amz-Signature=') >= 0) {
    return produce(photo, draft => {
      draft.url = queryString.parseUrl(photo.url).url;
    });
  }
  return photo;
};

/**
 * Function removes useless AWS signature from page.override.backgroundImage
 * @param {object} page
 * @returns {object}
 */
const fixPageObject = page => {
  if (isUrlValid(page?.override?.backgroundImage)) {
    const url = page.override.backgroundImage;
    // photo from media bucket with AWS signature
    if (url.indexOf('-media') >= 0 && url.indexOf('X-Amz-Signature=') >= 0) {
      return produce(page, draft => {
        draft.override.backgroundImage = queryString.parseUrl(url).url;
      });
    }
  }
  return page;
};

export const EditorContextProvider = props => {
  const [todayDate] = useState(formatDate(new Date(), UPDATED_AT_FORMAT));

  const modalContext = useModalContext();
  const { accessToken } = useUserContext();

  const [isLoading, setIsLoading] = useState(true);
  const [editorStore, setEditorStore] = useState();

  const fetchingDraftsData = useRef({});
  const fetchingPhotosInfo = useRef({});

  const isPhotoOnOurDomain = url => {
    // Don't upload when no domains are defined.
    if (props.whiteLabeledDomains.length === 0) {
      return true;
    }

    return props.whiteLabeledDomains.some(domain => !!url.match(domain));
  };

  useEffect(() => {
    const value = store.getCompressedJSON(props.storageKey, DEFAULT_VALUE);
    if (value.version === DEFAULT_VALUE.version) {
      // Delete useless empty items from editorStore
      setEditorStore(
        produce(value, draft => {
          Object.keys(value.items).forEach(key => {
            const { productId, updatedAt } = value.items[key];
            // productId is required
            if (!productId || !updatedAt) {
              delete draft.items[key];
              return;
            }

            if (isEditorItemExpired(todayDate, updatedAt, props.expiration)) {
              delete draft.items[key];
              return;
            }

            const { draftId, bundle, photos, pages, additionalInfo } = value.items[key];
            if (draftId != null) {
              draft.items[key].downloadDraftData = true;
              return;
            }

            if (bundle || photos.length > 0 || pages.length > 0) {
              return;
            }

            if (Object.keys(additionalInfo).length > 0) {
              return;
            }

            // delete empty item
            delete draft.items[key];
          });
        }),
      );
    } else {
      store.remove(props.storageKey);
      setEditorStore(DEFAULT_VALUE);
    }

    setIsLoading(false);
  }, []);

  const debounceErrorRef = useRef();

  useEffect(() => {
    if (!isLoading) {
      try {
        store.setCompressedJSON(
          props.storageKey,
          produce(editorStore, draft => {
            Object.entries(editorStore.items).forEach(([key, item]) => {
              if (item.draftId) {
                // if there is defined draftId, it's not needed to save data, because it's loaded from server
                draft.items[key] = pickExcept(
                  item,
                  'additionalInfo',
                  'favorite',
                  'pages',
                  'photos',
                );
              }
            });
          }),
          true,
        );
      } catch (e) {
        if (debounceErrorRef.current) {
          debounceErrorRef.current.cancel();
        }
        debounceErrorRef.current = debounce(() => {
          console.error({
            'Number of LocalStorage keys': store.size(),
            'LocalStorage keys': store.keys(),
            'Number of items in EditorStore': Object.keys(editorStore?.items ?? {}).length,
            'Length of EditorStore data': JSON.stringify(editorStore ?? {}).length,
            Error: e?.message,
          });
          window.Rollbar?.error(`LocalStorageError - "${props.storageKey}" (compressed)`);
        }, 1000);
        debounceErrorRef.current();
      }
    }
  }, [editorStore, isLoading]);

  const generateEditorItem = (productId, version, bundle = {}) => ({
    productId,
    version,
    updatedAt: todayDate,
    photos: [],
    pages: [],
    additionalInfo: {},
    favorite: {},
    bundle: bundle?.bundle ?? undefined,
    upsell: bundle?.upsell ?? undefined,
  });

  const updateEditorItem = (id, recipe) => {
    setEditorStore(value => {
      const newValue = produce(value, draft => {
        if (!value.items[id]) {
          return;
        }

        const { productId } = value.items[id];
        // when editorItem has productId defined, we can clear other items with same productId
        // dont remove basic editorItem (itemId === productId)
        // dont remove item with draftId
        if (productId) {
          Object.entries(value.items)
            .filter(
              ([key, item]) => item.productId === productId && key !== id && key !== productId,
            )
            .forEach(([key, item]) => {
              if (item.draftId == null) {
                delete draft.items[key];
              }
            });
        }

        recipe(draft.items[id], value.items[id]);
        draft.items[id].updatedAt = todayDate;
      });

      return produce(newValue, draft => {
        Object.entries(newValue.items).forEach(([key, item]) => {
          if (item.photos) {
            draft.items[key].photos = item.photos.map(fixPhotoObject);
          }
          if (item.pages) {
            draft.items[key].pages = item.pages.map(fixPageObject);
          }
        });
      });
    });
  };

  const setEditorItem = (id, data) => {
    setEditorStore(value =>
      produce(value, draft => {
        draft.items[id] = data;
      }),
    );
  };

  const downloadDraftData = async (item, template) => {
    const { id, draftId } = item;

    if (fetchingDraftsData.current[draftId] == null) {
      fetchingDraftsData.current[draftId] = 'downloading';
      const { data, error } = await syncApi(accessToken).getProductDraft(draftId);

      if (data) {
        const parsedData = parseData(template, data.data.data);

        updateEditorItem(id, draft => {
          delete draft.downloadDraftData;
          // data from draft has higher priority
          draft.draftName = data.data.draftName;
          draft.draftLastModified = data.updated_at;
          Object.entries(parsedData).forEach(([key, val]) => {
            draft[key] = val;
          });
          fetchingDraftsData.current[draftId] = 'downloaded';
        });
      } else if (error) {
        console.error(
          `SyncWarning - "downloadDraftData" - Draft #${draftId} was not downloaded, you probably work with old version!`,
        );
        updateEditorItem(id, draft => {
          delete draft.downloadDraftData;
          fetchingDraftsData.current[draftId] = 'error';
        });
      }
    }
  };

  const updatePhotos = id => {
    if (!editorStore.items[id].photos) {
      return;
    }

    const isFetching = (index, informationType) => {
      if (!fetchingPhotosInfo.current[id]) {
        fetchingPhotosInfo.current[id] = {};
      }

      const numberOfProcessingItems = Object.keys(fetchingPhotosInfo.current[id]).reduce(
        (acc, key) => {
          if (fetchingPhotosInfo.current[id][key][informationType] === true) {
            return acc + 1;
          }
          return acc;
        },
        0,
      );

      // max items in one iteration - then simple queue...
      if (numberOfProcessingItems >= 10) {
        return true;
      }

      if (fetchingPhotosInfo.current[id][index]) {
        if (fetchingPhotosInfo.current[id][index][informationType] === true) {
          return true;
        }
      }
      return false;
    };

    const setIsFetching = (index, informationType, value = true) => {
      if (!fetchingPhotosInfo.current[id][index]) {
        fetchingPhotosInfo.current[id][index] = {};
      }

      if (value) {
        fetchingPhotosInfo.current[id][index][informationType] = true;
      } else {
        delete fetchingPhotosInfo.current[id][index][informationType];
      }
    };

    editorStore.items[id].photos.forEach((photo, index) => {
      if (!photo) {
        return;
      }

      if (photo.alive === -1) {
        return;
      }

      if (!photo.width || !photo.height) {
        if (!isFetching(index, 'size')) {
          setIsFetching(index, 'size');
          setIsFetching(index, 'alive');

          (async () => {
            const { data: size, error } = await getImageSize(photo.url);

            updateEditorItem(id, draft => {
              try {
                if (error) {
                  draft.photos[index].alive = -1;
                  draft.photos[index].width = -1;
                  draft.photos[index].height = -1;
                } else {
                  draft.photos[index].alive = 1; // TODO string
                  draft.photos[index].width = size.width;
                  draft.photos[index].height = size.height;
                }
              } catch (e) {
                console.error(`PHOTO[${index}] of ${id} - size`, e);
              }
              setIsFetching(index, 'size', false);
              setIsFetching(index, 'alive', false);
            });
          })();
        }
      }

      if (!photo.alive) {
        if (!isFetching(index, 'alive')) {
          setIsFetching(index, 'alive');

          (async () => {
            const isAlive = await isPhotoStillExist(photo.url);

            updateEditorItem(id, draft => {
              try {
                if (!isAlive) {
                  draft.photos[index].alive = -1;
                } else {
                  draft.photos[index].alive = 1; // TODO string
                }
              } catch (e) {
                console.error(`PHOTO[${index}] of ${id} - isAlive`, e);
              }

              setIsFetching(index, 'alive', false);
            });
          })();
        }
      }

      if (!photo.validDomain) {
        if (!isFetching(index, 'newUrl')) {
          if (isPhotoOnOurDomain(photo.url)) {
            updateEditorItem(id, draft => {
              draft.photos[index].validDomain = true;
            });
            return;
          }

          setIsFetching(index, 'newUrl');

          (async () => {
            const { data, error } = await imageApi(accessToken).uploadImage(photo.url);

            if (error) {
              updateEditorItem(id, draft => {
                draft.photos[index].alive = -1;
              });
              return;
            }

            updateEditorItem(id, draft => {
              draft.photos[index].url = data.publicUrl;
              draft.photos[index].validDomain = true;
            });

            setIsFetching(index, 'newUrl', false);
          })();
        }
      }

      // when photo has defaultValue - I don't need to fetch
      if (!photo.datetime && !photo.defaultValue) {
        if (!isFetching(index, 'exif')) {
          setIsFetching(index, 'exif');

          (async () => {
            const { data, error } = await exif(getImagePreview(photo.url)).getDate();

            let datetime = data;
            if (error) {
              datetime = 'N/A';
            }

            updateEditorItem(id, draft => {
              try {
                draft.photos[index].datetime = datetime;
              } catch (e) {
                console.error(`PHOTO[${index}] of ${id} - datetime`, e);
              }
            });

            setIsFetching(index, 'exif', false);
          })();
        }
      }
    });
  };

  const useEditorItem = (id, template) => {
    const editorItem = useMemo(() => {
      if (isLoading) {
        return null;
      }

      if (!editorStore.items[id]) {
        setEditorItem(id, generateEditorItem(template.id, template.version));
        return null;
      }

      updatePhotos(id);

      const item = {
        draftName: '',
        pages: [],
        photos: [],
        favorite: {},
        ...editorStore.items[id],
        version: template.version,
        id,
      };

      if (item.downloadDraftData) {
        downloadDraftData(item, template);
        return null;
      }

      let pageLabelIndex = 0;

      return {
        ...item,
        pages: item.pages.map((page, index) => {
          const pageType = template.cover && index === 0 ? 'cover' : 'pages';
          const templatePage = getTemplatePage(template, page.id, pageType);
          let pageLabel;
          if (pageType === 'cover') {
            pageLabel = 'Cover';
          } else {
            pageLabelIndex += 1;
            pageLabel = `${pageLabelIndex}`;
            if (templatePage.span) {
              pageLabelIndex += 1;
              pageLabel = `${pageLabelIndex - 1} - ${pageLabelIndex}`;
            }
          }
          const pageNumber = template.cover ? index : index + 1;
          let placeholders;
          if (page.placeholders) {
            placeholders = page.placeholders.map(placeholder => {
              if (placeholder.id === 'page-num' && placeholder.type === PLACEHOLDER_TYPE.TEXT) {
                return {
                  ...placeholder,
                  data: { value: `${pageNumber}` },
                };
              }
              return placeholder;
            });
          }

          return {
            ...page,
            placeholders,
            index,
            pageNumber,
            pageLabel,
            pageType,
            ...(pageType === 'cover'
              ? {
                spine: hasPageLayers(templatePage)
                  ? undefined
                  : getSpineSize(template, item.pages.length - 1),
              }
              : {
                span: templatePage.span,
              }),
          };
        }),
        photos: item.photos.map(fixPhotoObject),
      };
    }, [isLoading, editorStore, id, template]);

    return editorItem;
  };

  return (
    <EditorContext.Provider
      value={useMemo(
        () => ({
          platform: props.platform,

          isLoading,

          accessToken,

          useEditorItem,

          getConfiguration(sku, version, override = {}) {
            const validOverride = Object.entries(override).reduce((acc, [key, value]) => {
              if (key != null && value != null) {
                acc[key] = value;
              }
              return acc;
            }, {});

            const prepareConfig = () => {
              const config = {
                editor: props.defaultEditor,
                numberOfPages: { xs: 1 },
                source: 'photos',
                preview: 'default',
                requiredPhotos: true,
                requiredTexts: false,
                devices: ['mobile', 'tablet', 'desktop'],
                withMultiProductDesignSelect: false,
                alternatives:
                  props.alternatives.find(skuAlternatives => skuAlternatives.indexOf(sku) >= 0) ||
                  [],
                backgroundsCategory: [],
              };

              if (props.configuration[sku]) {
                const { versions, ...skuOverride } = props.configuration[sku];
                const versionOverride = versions ? versions[version] ?? {} : {};

                return {
                  ...config,
                  ...skuOverride,
                  ...versionOverride,
                  ...validOverride,
                };
              }

              return {
                ...config,
                ...validOverride,
              };
            };

            const config = prepareConfig();

            if ([config.editor, config.preview].some(val => val.indexOf('book') >= 0)) {
              config.printType = 'book';
            }

            return config;
          },

          toast(children, params = {}) {
            modalContext.toast(children, params);
          },

          showModal(children, params = {}) {
            modalContext.show(children, params);
          },

          closeModal(key) {
            modalContext.close(key);
          },

          trackEvent(action, payload = {}) {
            props.onCustomEvent(action, payload);
          },

          onChoosePhotos(numberOfPhotos) {
            props.onChoosePhotos(numberOfPhotos);
          },

          onUploadPhotos(numberOfPhotos) {
            props.onUploadPhotos(numberOfPhotos);
          },

          onPreviewPrints(sku) {
            props.onPreviewPrints(sku);
          },

          readyToEdit(id) {
            setEditorStore(value =>
              produce(value, draft => {
                if (value.items[id]) {
                  draft.items[id].readyToEdit = true;
                }
              }),
            );
          },

          isReadyToEdit(id) {
            if (editorStore.items[id]) {
              if (editorStore.items[id].readyToEdit !== true) {
                return false;
              }
            }
            return true;
          },

          changeItemId(id, newId) {
            setEditorStore(value =>
              produce(value, draft => {
                draft.items[newId] = value.items[id];

                // Old item is deleted automatically
              }),
            );
          },

          createItem(productId, version, bundle = {}) {
            setEditorItem(productId, generateEditorItem(productId, version, bundle));
          },

          setItem(itemId, template, data) {
            setEditorItem(itemId, {
              productId: template.id,
              version: template.version,
              updatedAt: todayDate,
              ...data,
            });
          },

          editDraft(
            itemId,
            productId,
            draftId,
            draftName = '',
            draftLastModified = undefined,
            template,
            data,
            bundle = null,
          ) {
            setEditorItem(itemId, {
              productId,
              version: template.version,
              updatedAt: todayDate,
              draftId,
              draftName,
              draftLastModified,
              bundle,
              ...parseData(template, data),
            });
          },

          editCartItem(cartItem, template) {
            const id = cartItem.id;
            const productId = cartItem.productId;
            const draftId = cartItem.draftId;
            const draftName = cartItem.draftName;
            const draftLastModified = cartItem.draftLastModified;
            const data = cartItem.data;
            const bundle = cartItem.bundle
              ? {
                id: cartItem.bundle.id,
                name: cartItem.name,
                secondaryProducts: cartItem.bundle.secondaryProducts,
              }
              : null;
            const upsell = cartItem.upsell;

            setEditorItem(id, {
              id,
              productId,
              version: template.version,
              updatedAt: todayDate,
              draftId: draftId || undefined,
              draftName: draftName || '',
              draftLastModified: draftLastModified || undefined,
              bundle,
              upsell,
              ...parseData(template, data),
            });
          },

          clearItem(productId, version, itemId) {
            setEditorItem(itemId, generateEditorItem(productId, version));
          },

          removeItem(id) {
            setEditorStore(value =>
              produce(value, draft => {
                delete draft.items[id];
              }),
            );
          },

          removeBundle(id) {
            updateEditorItem(id, draft => {
              draft.bundle = null;
              draft.upsell = null;
            });
          },

          addPhotos(id, photos = []) {
            updateEditorItem(id, (draft, value) => {
              draft.photos = uniqBy([...value.photos, ...photos], 'key');
            });
          },

          setDraftName(id, draftName) {
            updateEditorItem(id, draft => {
              draft.draftName = draftName;
            });
          },

          setDraftId(id, draftId) {
            updateEditorItem(id, draft => {
              draft.draftId = draftId;
            });
          },

          setDraftLastModified(id, lastModified) {
            updateEditorItem(id, draft => {
              draft.draftLastModified = lastModified;
            });
          },

          setPhotos(id, photos = []) {
            updateEditorItem(id, draft => {
              draft.photos = photos;
            });
          },

          updatePhoto(id, key, recipe) {
            updateEditorItem(id, (draft, value) => {
              const index = value.photos.findIndex(photo => photo.key === key);
              if (index >= 0) {
                const photo = value.photos[index];
                draft.photos[index] = produce(photo, recipe(photo));
              }
            });
          },

          addPhotoToPlaceholder(id, pageIndex, placeholderId, photo) {
            if (photo) {
              updateEditorItem(id, (draft, value) => {
                const placeholderIndex = value.pages[pageIndex].placeholders.findIndex(
                  placeholder => placeholder.id === placeholderId,
                );

                const photoData = {
                  photo_key: photo.key,
                  x: 0.5,
                  y: 0.5,
                  scale: 1,
                  orientation: 0,
                };

                if (placeholderIndex >= 0) {
                  draft.pages[pageIndex].placeholders[placeholderIndex].data = photoData;
                } else {
                  draft.pages[pageIndex].placeholders.push({
                    id: placeholderId,
                    type: 'photo',
                    data: photoData,
                  });
                }
              });
            }
          },

          setTextData(id, pageIndex, placeholderId, textValue = '', override = undefined) {
            updateEditorItem(id, (draft, value) => {
              const placeholderIndex = value.pages[pageIndex].placeholders.findIndex(
                placeholder => placeholder.id === placeholderId,
              );

              if (placeholderIndex >= 0) {
                const placeholder = value.pages[pageIndex].placeholders[placeholderIndex];

                draft.pages[pageIndex].placeholders[placeholderIndex].data = {
                  value: textValue,
                };
                draft.pages[pageIndex].placeholders[placeholderIndex].override = {
                  ...placeholder.override,
                  ...override,
                };
              } else {
                draft.pages[pageIndex].placeholders.push({
                  id: placeholderId,
                  type: 'text',
                  data: {
                    value: textValue,
                  },
                  override,
                });
              }

              // TODO - TOC for old version book editor
              if (TOC.isPage(value.pages[pageIndex])) {
                const headingPlaceholder = {
                  id: placeholderId,
                  type: 'text',
                  data: {
                    value: textValue,
                  },
                };
                if (TOC.isHeadingPlaceholder(headingPlaceholder)) {
                  const index = TOC.getHeadingIndex(headingPlaceholder);

                  const pageNumberPlaceholder = value.pages[pageIndex].placeholders.find(
                    placeholder => placeholder.id === TOC.getPageNumberPlaceholderId(index),
                  );

                  if (pageNumberPlaceholder) {
                    const pgnum = pageNumberPlaceholder.data.value;
                    if (value.pages[pgnum]) {
                      draft.pages[pgnum].caption = textValue;
                    }
                  }
                }
              }
            });
          },

          setPhotoData(id, pageIndex, placeholderId, data) {
            updateEditorItem(id, (draft, value) => {
              const placeholderIndex = value.pages[pageIndex].placeholders.findIndex(
                placeholder => placeholder.id === placeholderId,
              );

              if (placeholderIndex >= 0) {
                if (data) {
                  const photoIndex = value.photos.findIndex(p => p.url === data.url);
                  const photo = value.photos[photoIndex];

                  draft.pages[pageIndex].placeholders[placeholderIndex].data = {
                    photo_key: photo.key,
                    x: data.x,
                    y: data.y,
                    scale: data.scale,
                    orientation: data.orientation,
                  };
                } else {
                  draft.pages[pageIndex].placeholders[placeholderIndex].data = {};
                }
              }
            });
          },

          updateUpsell(id, upsell = {}) {
            updateEditorItem(id, (draft, value) => {
              if (value.upsell) {
                draft.upsell = {
                  ...value.upsell,
                  ...upsell,
                };
              }
            });
          },

          setPages(id, pages = []) {
            updateEditorItem(id, draft => {
              draft.pages = pages;
            });
          },

          addPages(id, newPages = [], { index = -1, sortByTags = false } = {}) {
            updateEditorItem(id, (draft, value) => {
              if (index >= 0) {
                // insert to some index
                const { pages } = value;
                draft.pages = [...pages.slice(0, index), ...newPages, ...pages.slice(index)];
              } else if (sortByTags) {
                // sort by tags
                newPages.forEach(newPage => {
                  const lastIndex = findLastIndex(draft.pages, page =>
                    contains(page.tags, newPage.tags, true),
                  );
                  if (lastIndex >= 0) {
                    draft.pages.splice(lastIndex + 1, 0, newPage);
                  } else {
                    draft.pages.push(newPage);
                  }
                });
              } else {
                // insert to the end
                draft.pages.push(...newPages);
              }
            });
          },

          updateTOCPages(id, tocPageIds = [], newTOCdata = {}, hasCover = false) {
            const {
              pages: newTOCPages = [],
              photos: newTOCPhotos = [],
              startIndex = 0,
            } = newTOCdata;

            updateEditorItem(id, (draft, value) => {
              const originalPages = value.pages.filter(page => {
                if (tocPageIds.indexOf(page.id) >= 0) {
                  return false;
                }
                return true;
              });

              const newPages = [];
              if (hasCover) {
                const [cover, ...pages] = originalPages;
                newPages.push(cover);
                newPages.push(...pages);
                newPages.splice(startIndex + 1, 0, ...newTOCPages);
              } else {
                newPages.push(...originalPages);
                newPages.splice(startIndex, 0, ...newTOCPages);
              }

              draft.pages = newPages;

              draft.photos.push(
                ...newTOCPhotos.filter(
                  newPhoto => !value.photos.some(photo => photo.key === newPhoto.key),
                ),
              );
            });
          },

          duplicatePage(id, index) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = [
                ...value.pages.slice(0, index + 1),
                { ...value.pages[index], key: uuid() },
                ...value.pages.slice(index + 1),
              ];
            });
          },

          duplicatePages(id, pageKeys = []) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.reduce((acc, page) => {
                acc.push(page);
                if (pageKeys.indexOf(page.key) >= 0) {
                  acc.push({ ...page, key: uuid() });
                }
                return acc;
              }, []);
            });
          },

          removePage(id, index) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.filter((_, i) => i !== index);
            });
          },

          removePageByIds(id, pageIds = []) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.filter(page => pageIds.indexOf(page.id) < 0);
            });
          },

          removePageByKeys(id, pageKeys = []) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.filter(page => pageKeys.indexOf(page.key) < 0);
            });
          },

          movePage(id, withCover, oldIndex, newIndex, numberOfPages = 1) {
            updateEditorItem(id, (draft, value) => {
              if (withCover) {
                const [cover, ...pages] = value.pages;
                draft.pages = [cover, ...arrayMove(pages, oldIndex, newIndex, numberOfPages)];
              } else {
                draft.pages = arrayMove(value.pages, oldIndex, newIndex, numberOfPages);
              }
            });
          },

          movePages(id, index, length, newIndex) {
            updateEditorItem(id, (draft, value) => {
              const [pages, movingPages] = value.pages.reduce(
                (acc, page, idx) => {
                  if (idx < index || idx >= index + length) {
                    acc[0].push(page);
                  } else {
                    acc[1].push(page);
                  }
                  return acc;
                },
                [[], []],
              );

              pages.splice(newIndex, 0, ...movingPages);

              draft.pages = pages;
            });
          },

          setAdditionalInfo(id, additionalInfo = {}) {
            updateEditorItem(id, draft => {
              draft.additionalInfo = additionalInfo;
            });
          },


          updateAdditionalInfo(id, additionalInfo = {}) {
            updateEditorItem(id, (draft, value) => {
              draft.additionalInfo = {
                ...value.additionalInfo,
                ...additionalInfo,
              }
            });
          },

          changePage(id, page, { pageIndex, pageKey } = {}) {
            // When TOC page is changed, we will update captions of pages defined in TOC page
            // TOC is used only in book, so pages[0] is cover, pages[1] is actually first page
            const captions = {};

            // THIS CODE IS COMMENTED, BECAUSE IT IS USELESS TO UDPATE CAPTION FORM TOC PAGE
            // NOW, CAPTION IS DEFINED IN PAGE SETTINGS PANEL.
            // CAN BE REMOVED.

            // if (TOC.isPage(page)) {
            //   page.placeholders.forEach(placeholder => {
            //     if (TOC.isHeadingPlaceholder(placeholder)) {
            //       const index = TOC.getHeadingIndex(placeholder);
            //       const value = placeholder.data.value;

            //       if (!captions[index]) {
            //         captions[index] = {};
            //       }
            //       captions[index].caption = value;
            //     }

            //     if (TOC.isPageNumberPlaceholder(placeholder)) {
            //       const index = TOC.getPageNumberIndex(placeholder);
            //       const value = parseInt(placeholder.data.value, 10);

            //       if (!captions[index]) {
            //         captions[index] = {};
            //       }
            //       captions[index].pgnum = value;
            //     }
            //   });
            // }

            // // When PAGE is blank, and there is no caption defined, we can use caption from placeholder;
            // const getPageCaption = () => {
            //   if (!page.caption) {
            //     if (page.id.indexOf('blank') >= 0) {
            //       const placeholderCaption = page.placeholders.find(
            //         placeholder =>
            //           placeholder.type === 'text' && placeholder.id.indexOf('caption') >= 0,
            //       );
            //       if (placeholderCaption) {
            //         return placeholderCaption.data.value ?? undefined;
            //       }
            //     }
            //   }

            //   return page.caption;
            // };

            updateEditorItem(id, (draft, value) => {
              const index = pageIndex ?? value.pages.findIndex(p => p.key === pageKey);
              if (index == null) {
                return;
              }
              draft.pages[index] = {
                ...page,
                // TODO again, it is useless - because I need real caption, not some default. This logic has to be moved to serverless TOC generator
                // caption: getPageCaption(),
              };

              Object.entries(captions).forEach(([, { caption, pgnum }]) => {
                if (caption == null || pgnum == null) {
                  return;
                }

                if (value.pages[pgnum]) {
                  draft.pages[pgnum].caption = caption;
                }
              });
            });
          },

          changeProduct(id, productId, newProductId, data = {}) {
            if (productId !== newProductId) {
              setEditorStore(value =>
                produce(value, draft => {
                  const newValue = { ...value.items[id], productId: newProductId, ...data };
                  // When I switch product SKU - I need to use all templatePlaceholders for cover
                  draft.items[id] = produce(newValue, secondDraft => {
                    secondDraft.pages[0].withTemplatePlaceholders = true;
                  });
                }),
              );
            }
          },

          removePlaceholder(id, pageIndex, placeholderId) {
            updateEditorItem(id, (draft, value) => {
              const placeholders = (value.pages[pageIndex].placeholders || []).filter(
                p => p.id !== placeholderId,
              );

              draft.pages[pageIndex].placeholders = placeholders;
            });
          },

          setCustomBackgroundImage(id, backgroundImage, predicate = () => true) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.map((page, index) => {
                if (predicate(page, index)) {
                  return {
                    ...page,
                    override: {
                      ...page.override,
                      backgroundColor: undefined,
                      backgroundImage,
                    },
                    placeholders: page.placeholders.filter(p => p.id !== 'custom-background'),
                  };
                }
                return page;
              });
            });
          },

          setBackgroundOpacity(id, opacity, predicate = () => true) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.map((page, index) => {
                if (predicate(page, index)) {
                  return {
                    ...page,
                    override: {
                      ...page.override,
                      backgroundOpacity: opacity,
                    },
                  };
                }
                return page;
              });
            });
          },

          overridePages(id, override = {}, predicate = () => true) {
            updateEditorItem(id, (draft, value) => {
              draft.pages = value.pages.map((page, index) => {
                if (predicate(page, index)) {
                  return {
                    ...page,
                    override: {
                      ...page.override,
                      ...override,
                    },
                  };
                }
                return page;
              });
            });
          },

          swapPhotos(id, pageIndexA, placeholderA, photoKeyA, pageIndexB, placeholderB, photoKeyB) {
            updateEditorItem(id, (draft, value) => {
              try {
                const placeholderIndexA = value.pages[pageIndexA].placeholders.findIndex(
                  p => p.id === placeholderA,
                );

                const placeholderIndexB = value.pages[pageIndexB].placeholders.findIndex(
                  p => p.id === placeholderB,
                );

                if (photoKeyB) {
                  draft.pages[pageIndexA].placeholders[placeholderIndexA].data = {
                    photo_key: photoKeyB,
                    x: 0.5,
                    y: 0.5,
                    scale: 1,
                    orientation: 0,
                  };

                  draft.pages[pageIndexB].placeholders[placeholderIndexB].data = {
                    photo_key: photoKeyA,
                    x: 0.5,
                    y: 0.5,
                    scale: 1,
                    orientation: 0,
                  };
                } else {
                  draft.pages[pageIndexA].placeholders[placeholderIndexA].data = {};

                  draft.pages[pageIndexB].placeholders[placeholderIndexB].data = {
                    photo_key: photoKeyA,
                    x: 0.5,
                    y: 0.5,
                    scale: 1,
                    orientation: 0,
                  };
                }
              } catch (e) {
                console.error('Photo Swap Issue', id);
              }
            });
          },

          addEvents(id, newEvents = []) {
            updateEditorItem(id, (draft, value) => {
              const events = value.additionalInfo?.events ?? [];

              draft.additionalInfo = {
                ...value.additionalInfo,
                events: uniqBy(
                  [...events, ...newEvents],
                  event => `${event.date}-${event.description}`,
                ),
              };
            });
          },

          filterEvents(id, predicate = () => false) {
            updateEditorItem(id, (draft, value) => {
              const events = value.additionalInfo?.events ?? [];

              draft.additionalInfo = {
                ...value.additionalInfo,
                events: events.filter(predicate),
              };
            });
          },

          addFavorite(id, type, item) {
            if (!item.id) {
              console.log(`Your favorite ${type} has no 'id' attribute!`);
              return;
            }

            updateEditorItem(id, (draft, value) => {
              const favorite = value.favorite ?? {};
              let favoriteItems = favorite[type] ?? {};
              // TODO this should be remove, it is only to old DEV approach, when favorite was array
              // Now it's object - due to performance
              if (Array.isArray(favoriteItems)) {
                favoriteItems = {};
              }

              draft.favorite = {
                ...favorite,
                [type]: {
                  ...favoriteItems,
                  [item.id]: item,
                },
              };
            });
          },

          removeFavorite(id, type, itemId) {
            updateEditorItem(id, (draft, value) => {
              const favorite = value.favorite ?? {};
              let favoriteItems = { ...favorite[type] } ?? {};
              // TODO this should be remove, it is only to old DEV approach, when favorite was array
              // Now it's object - due to performance
              if (Array.isArray(favoriteItems)) {
                favoriteItems = {};
              }

              if (favoriteItems[itemId]) {
                delete favoriteItems[itemId];
              }

              draft.favorite = {
                ...favorite,
                [type]: favoriteItems,
              };
            });
          },

          removePhoto(id, photoKey) {
            updateEditorItem(id, (draft, value) => {
              draft.photos = value.photos.filter(photo => photo.key !== photoKey);
            });
          },
        }),
        [isLoading, accessToken, editorStore],
      )}
    >
      {props.children}
    </EditorContext.Provider>
  );
};

EditorContextProvider.propTypes = {
  platform: PropTypes.string.isRequired,
  storageKey: PropTypes.string,
  defaultEditor: PropTypes.string,
  expiration: PropTypes.number,
  configuration: PropTypes.object,
  alternatives: PropTypes.array,
  whiteLabeledDomains: PropTypes.array,
  onChoosePhotos: PropTypes.func,
  onUploadPhotos: PropTypes.func,
  onPreviewPrints: PropTypes.func,
  onCustomEvent: PropTypes.func,
  children: PropTypes.node,
};

EditorContextProvider.defaultProps = {
  storageKey: 'editor_next',
  defaultEditor: 'default',
  expiration: 30,
  configuration: {},
  alternatives: [],
  whiteLabeledDomains: [],
  onChoosePhotos: () => { },
  onUploadPhotos: () => { },
  onPreviewPrints: () => { },
  onCustomEvent: () => { },
};

const useEditorContext = () => {
  return useContext(EditorContext);
};

export default useEditorContext;
